import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Fish,
  ArrowRight,
  ExternalLink,
  Bookmark,
  BookmarkCheck,
  TrendingUp,
  TrendingDown,
  Building2,
  AlertTriangle,
  Clock,
  Filter,
} from 'lucide-react';
import {
  WhaleAlert,
  ALERT_TYPE_LABELS,
  SIGNIFICANCE_COLORS,
} from '../../types/whale-tracking';

export function WhaleAlertFeed() {
  const [filter, setFilter] = useState<'all' | 'critical' | 'saved'>('all');
  const [savedAlerts, setSavedAlerts] = useState<Set<string>>(new Set());

  // Mock alerts - will be replaced with real-time WebSocket data
  const alerts: WhaleAlert[] = [
    {
      id: '1',
      timestamp: Date.now() - 5 * 60 * 1000,
      type: 'exchange_flow',
      token: {
        address: '0x0',
        symbol: 'ETH',
        name: 'Ethereum',
        decimals: 18,
        price: 2245.50,
      },
      title: 'Retiro masivo de Binance',
      description: '15,000 ETH ($33.7M) retirados de Binance a wallet desconocida. Posible acumulacion institucional.',
      significance: 'critical',
      isRead: false,
      isSaved: false,
      transaction: {
        id: 't1',
        txHash: '0x1234...abcd',
        timestamp: Date.now() - 5 * 60 * 1000,
        from: {
          address: '0x28C6c06298d514Db089934071355E5743bf21d60',
          label: 'Binance Hot Wallet',
          category: 'exchange',
          totalValue: '0',
          lastActivity: Date.now(),
          isTracked: true,
          tags: ['exchange', 'binance'],
        },
        to: {
          address: '0xabcd...1234',
          category: 'unknown',
          totalValue: '0',
          lastActivity: Date.now(),
          isTracked: false,
          tags: [],
        },
        token: {
          address: '0x0',
          symbol: 'ETH',
          name: 'Ethereum',
          decimals: 18,
          price: 2245.50,
        },
        amount: '15000',
        valueUSD: '33682500',
        type: 'exchange_withdrawal',
        significance: 'critical',
        marketImpact: {
          priceChangePercent: 1.2,
          volumeImpact: 'moderate',
          confidence: 78,
          prediction: 'bullish',
          reasoning: 'Large exchange withdrawals typically indicate accumulation and reduced sell pressure.',
        },
      },
    },
    {
      id: '2',
      timestamp: Date.now() - 15 * 60 * 1000,
      type: 'accumulation_pattern',
      token: {
        address: '0x1234567890123456789012345678901234567890',
        symbol: 'NDH',
        name: 'Nardium Token',
        decimals: 18,
        price: 0.85,
      },
      title: 'Patron de acumulacion detectado',
      description: '5 wallets whale han acumulado 2.5M NDH en las ultimas 4 horas. Tendencia alcista emergente.',
      significance: 'high',
      isRead: false,
      isSaved: false,
    },
    {
      id: '3',
      timestamp: Date.now() - 32 * 60 * 1000,
      type: 'large_transfer',
      token: {
        address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
        symbol: 'WBTC',
        name: 'Wrapped Bitcoin',
        decimals: 8,
        price: 43250,
      },
      title: 'Transferencia whale WBTC',
      description: '500 WBTC ($21.6M) transferidos entre wallets desconocidas. Monitorear destino.',
      significance: 'high',
      isRead: true,
      isSaved: false,
    },
    {
      id: '4',
      timestamp: Date.now() - 1 * 60 * 60 * 1000,
      type: 'exchange_flow',
      token: {
        address: '0x0',
        symbol: 'ETH',
        name: 'Ethereum',
        decimals: 18,
        price: 2245.50,
      },
      title: 'Deposito a Coinbase',
      description: '8,500 ETH ($19.1M) depositados en Coinbase. Posible presion de venta.',
      significance: 'medium',
      isRead: true,
      isSaved: false,
    },
    {
      id: '5',
      timestamp: Date.now() - 2 * 60 * 60 * 1000,
      type: 'tracked_wallet_activity',
      token: {
        address: '0x514910771AF9Ca656af840dff83E8264EcF986CA',
        symbol: 'LINK',
        name: 'Chainlink',
        decimals: 18,
        price: 14.25,
      },
      title: 'Wallet rastreada activa',
      description: 'Una wallet que sigues ha comprado 150,000 LINK ($2.1M) en DEX.',
      significance: 'medium',
      isRead: false,
      isSaved: true,
    },
    {
      id: '6',
      timestamp: Date.now() - 3 * 60 * 60 * 1000,
      type: 'distribution_pattern',
      token: {
        address: '0x0',
        symbol: 'ETH',
        name: 'Ethereum',
        decimals: 18,
        price: 2245.50,
      },
      title: 'Distribucion whale detectada',
      description: 'Una wallet antigua (2017) ha comenzado a distribuir 50,000 ETH gradualmente.',
      significance: 'critical',
      isRead: true,
      isSaved: true,
    },
  ];

  const filteredAlerts = alerts.filter((alert) => {
    if (filter === 'critical') return alert.significance === 'critical';
    if (filter === 'saved') return savedAlerts.has(alert.id) || alert.isSaved;
    return true;
  });

  const toggleSave = (alertId: string) => {
    setSavedAlerts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const formatTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (60 * 1000));
    const hours = Math.floor(diff / (60 * 60 * 1000));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getSignificanceIcon = (significance: string) => {
    switch (significance) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4" />;
      case 'high':
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <Fish className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'exchange_flow':
        return <Building2 className="h-5 w-5" />;
      case 'accumulation_pattern':
        return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'distribution_pattern':
        return <TrendingDown className="h-5 w-5 text-red-500" />;
      default:
        return <Fish className="h-5 w-5" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-500" />
        <div className="flex gap-2">
          {(['all', 'critical', 'saved'] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? 'default' : 'outline'}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'Todas' : f === 'critical' ? 'Criticas' : 'Guardadas'}
            </Button>
          ))}
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-3">
        {filteredAlerts.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Fish className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400">
                No hay alertas que mostrar
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredAlerts.map((alert) => {
            const isSaved = savedAlerts.has(alert.id) || alert.isSaved;

            return (
              <Card
                key={alert.id}
                className={`transition-all ${!alert.isRead ? 'border-l-4 border-l-blue-500' : ''} ${
                  alert.significance === 'critical' ? 'border-red-500/30 bg-red-500/5' : ''
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`p-2 rounded-lg ${SIGNIFICANCE_COLORS[alert.significance]}`}>
                      {getTypeIcon(alert.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                          {alert.title}
                        </h3>
                        <Badge variant="outline" className={SIGNIFICANCE_COLORS[alert.significance]}>
                          {getSignificanceIcon(alert.significance)}
                          <span className="ml-1 capitalize">{alert.significance}</span>
                        </Badge>
                        <Badge variant="outline">
                          {ALERT_TYPE_LABELS[alert.type]}
                        </Badge>
                      </div>

                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                        {alert.description}
                      </p>

                      {/* Token & Time */}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center text-white text-[10px] font-bold">
                            {alert.token.symbol.charAt(0)}
                          </div>
                          {alert.token.symbol}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTimeAgo(alert.timestamp)}
                        </span>
                      </div>

                      {/* Market Impact (if available) */}
                      {alert.transaction?.marketImpact && (
                        <div className="mt-3 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                              Prediccion IA:
                            </span>
                            <Badge
                              className={
                                alert.transaction.marketImpact.prediction === 'bullish'
                                  ? 'bg-green-500/20 text-green-500'
                                  : alert.transaction.marketImpact.prediction === 'bearish'
                                  ? 'bg-red-500/20 text-red-500'
                                  : 'bg-slate-500/20 text-slate-500'
                              }
                            >
                              {alert.transaction.marketImpact.prediction === 'bullish' ? 'Alcista' :
                               alert.transaction.marketImpact.prediction === 'bearish' ? 'Bajista' : 'Neutral'}
                              {' '}({alert.transaction.marketImpact.confidence}%)
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-400">
                            {alert.transaction.marketImpact.reasoning}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleSave(alert.id)}
                        className={isSaved ? 'text-amber-500' : ''}
                      >
                        {isSaved ? (
                          <BookmarkCheck className="h-4 w-4" />
                        ) : (
                          <Bookmark className="h-4 w-4" />
                        )}
                      </Button>
                      {alert.transaction && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            window.open(
                              `https://etherscan.io/tx/${alert.transaction?.txHash}`,
                              '_blank'
                            );
                          }}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
